<template>
    <div class="container mt-4">
        <div class="row justify-content-center">
            <div class="col-md-12">
                <div class="d-flex justify-content-between align-items-center mb-4">
                    <h1 class="mb-0">Patients that will be migrated</h1>
                </div>

                <!-- Button and Count for Active Patients -->
                <button @click="toggleShowActive" class="btn btn-primary mb-2 me-2">
                    Show Active Patients ({{ activePatients.length }})
                </button>
                <!-- Active Patients Table -->
                <div v-if="showActive" class="border rounded border-gray-400 p-2 mb-4">
                    <h2>Active Patients</h2>
                    <table class="table">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Email</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="patient in paginatedActivePatients" :key="patient.id">
                            <td>{{ patient.id || 'N/A' }}</td>
                            <td>{{ patient.first_name }}</td>
                            <td>{{ patient.last_name }}</td>
                            <td>{{ patient.email || 'None' }}</td>
                        </tr>
                        </tbody>
                    </table>

                    <!-- Pagination controls for active patients -->
                    <div class="d-flex justify-content-between mb-2">
                        <button @click="currentPageActive--" :disabled="currentPageActive <= 1"
                                class="btn btn-secondary">Previous
                        </button>
                        <button @click="currentPageActive++" :disabled="currentPageActive >= totalPagesActive"
                                class="btn btn-secondary">Next
                        </button>
                    </div>
                    <p>Page: {{ currentPageActive }} / {{ totalPagesActive }}</p>
                </div>

                <!-- Button and Count for Inactive Patients -->
                <button v-if="inactivePatients.length > 0" @click="toggleShowInactive" class="btn btn-warning mb-2">
                    Show Inactive Patients ({{ inactivePatients.length }})
                </button>


                <!-- Inactive Patients Table -->
                <div v-if="showInactive" class="border rounded border-gray-400 p-2 mb-4">
                    <h2>Patients that are currently inactive:</h2>
                    <table class="table">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Email</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="patient in paginatedInactivePatients" :key="patient.id">
                            <td>{{ patient.id || 'N/A' }}</td>
                            <td>{{ patient.first_name }}</td>
                            <td>{{ patient.last_name }}</td>
                            <td>{{ patient.email }}</td>

                        </tr>
                        </tbody>
                    </table>

                    <!-- Pagination controls for inactive patients -->
                    <div class="d-flex justify-content-between mb-2">
                        <button @click="currentPageInactive--" :disabled="currentPageInactive <= 1"
                                class="btn btn-secondary">Previous
                        </button>
                        <button @click="currentPageInactive++" :disabled="currentPageInactive >= totalPagesInactive"
                                class="btn btn-secondary">Next
                        </button>
                    </div>
                    <p>Page: {{ currentPageInactive }} / {{ totalPagesInactive }}</p>
                </div>

            </div>
        </div>

    </div>
    <div class="d-flex justify-content-center align-items-center mt-10">
        <div class="card" style="width: 24rem;">
            <div class="card-body">
                <h5 class="card-title text-center">Select Patients to Migrate</h5>
                <div class="d-flex justify-content-between">
                    <div class="form-group position-relative">
                        <label for="importType">Choose patients to import:</label>
                        <select id="importType" v-model="importChoice" class="form-control custom-select">
                            <option disabled value="" selected>Select an option</option>
                            <option v-if="inactivePatients.length > 0" value="active">Active Only</option>
                            <option value="both">All</option>
                        </select>
                        <div class="dropdown-arrow"></div>
                    </div>
                    <button class="btn btn-primary align-self-end" @click="migratePatients">Migrate</button>
                </div>
            </div>
        </div>
    </div>

</template>

<script setup>
    import {ref, computed, defineProps, defineEmits, onMounted} from 'vue';

    const emit = defineEmits([
        'close-modals',
        'migration-complete'
    ]);
    const props = defineProps({
        activePatients: {
            type: Array,
            default: () => []
        },
        inactivePatients: {
            type: Array,
            default: () => []
        },
        org: {
            type: Object,
            default: () => []
        }
    });

    const showActive = ref(false);
    const showInactive = ref(false);
    const importChoice = ref('both');
    const currentPageActive = ref(1);
    const locationID = ref(null);
    const pageSizeActive = 10;
    const totalPagesActive = computed(() => Math.ceil(props.activePatients.length / pageSizeActive));

    const paginatedActivePatients = computed(() => {
        const start = (currentPageActive.value - 1) * pageSizeActive;
        const end = start + pageSizeActive;
        return props.activePatients.slice(start, end);
    });

    const currentPageInactive = ref(1);
    const pageSizeInactive = 10;
    const totalPagesInactive = computed(() => Math.ceil(props.inactivePatients.length / pageSizeInactive));

    const paginatedInactivePatients = computed(() => {
        const start = (currentPageInactive.value - 1) * pageSizeInactive;
        const end = start + pageSizeInactive;
        return props.inactivePatients.slice(start, end);
    });

    const toggleShowActive = () => {
        showActive.value = !showActive.value;
    };

    const toggleShowInactive = () => {
        showInactive.value = !showInactive.value;
    };

    const migratePatients = async () => {
        let dme_id = props.org.dme_id || props.org._dme_id;
        await getLocationId(dme_id);

        let patients;

        if (importChoice.value === 'active') {
            patients = props.activePatients ? props.activePatients.map(patient => patient.id) : [];
        } else if (importChoice.value === 'inactive') {
            patients = props.inactivePatients ? props.inactivePatients.map(patient => patient.id) : [];
        } else {
            const allPatients = [...props.activePatients, ...props.inactivePatients];
            patients = allPatients ? allPatients.map(patient => patient.id) : [];
        }


        axios.post('/api/migrate/patients', {
            patients: patients,
            location_id: locationID.value
        })
            .then(response => {
                console.info(response);
            })
            .catch(error => {
                console.error(error);
            });


        // close the modal
        emit('migration-complete');
        emit('close-modals');
    };

    const getLocationId = async (dme_id) => {
        try {
            const response = await axios.post('/api/orgs/get-location', {dme_id});
            locationID.value = response.data;
        } catch (error) {
            console.error(error);
        }
    };

</script>


<style scoped>
    .dropdown-arrow {
        position: absolute;
        top: 50%;
        right: 16px;
        transform: translateY(80%);
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 8px solid black;
        pointer-events: none;
    }
</style>
